import type { IBatchStore } from './batch-store-types';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { OUTPUT_TTL } from '../../constants/Pac';
import { CONTINUOUS_FEED_BUTTON_PRESS_DURATION } from '../../constants/ContinuousPress';
import { toNumber } from '../../utils/numbers';
import convertUnit from '../../utils/unit-converter';
import logger from '../../utils/logger';
import { aeratorSwitch } from './device-switches/aerator-switch';
import { vibratorSwitch } from './device-switches/vibrator-switch';
import {
  isFinishDischargeDevice,
  onFinishDischargeButtonPress,
} from './pump-discharge-operations';
import { showConfirmModal } from '../../modules/modals/confirmService';
import {
  RemoteBatchData,
  RemoteControlEventType,
} from '../../containers/Batch/RemoteBatchController';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { MixerType } from '../../constants/BatchTypeEnums';
import type {
  IBin,
  IMixer,
  IPump,
  IScale,
} from '../../../types/device/DeviceModels';
import type { IMaterial } from '../../../types/material/MaterialModels';
import type { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';
import { ALLOWED_MIXER_STATUSES_FOR_NEXT_TICKET_BATCHING } from './batch-action-mixin';

export const manualFeedMixin = {

  onPumpButtonPress(this: IBatchStore, pump: IPump) {
    if (this.canSendRemoteInstruction()) {
      return;
    }

    // @ts-ignore
    this.clearTimeout(this[`pump-inventory-monitoring-timer-${pump.id}`]);
    // @ts-ignore
    this.clearTimeout(this[`pump-button-release-timer-${pump.id}`]);
    const pumpStatus = this.getDeviceStatus(pump.id);
    const pumpWeight = this.getPumpWeight(pump);

    this.deviceStatus.set(pump.id, {
      ...pumpStatus,
      manualStartWeight: pumpStatus.manualStartWeight ? pumpStatus.manualStartWeight : pumpWeight,
      manualFeedStarted: true,
    });

    this.setOutput(pump.gatePNo, OUTPUT_TTL);

    const runningData = this.running.find(v => v.uid === pumpStatus.uid && v.currentDrop === pumpStatus.currentDrop);
    if (runningData) {
      const pumpData = runningData.pumps[pump.id];
      if (pumpData?.initialValue === null) {
        logger.log('setting up the initial value for pump, manual start', pumpWeight, pump.description);
        pumpData.initialValue = pumpWeight;
        pumpData.initialWaterValue = pumpWeight;
      }

    }
  },

  onPumpButtonRelease(this: IBatchStore, pump: IPump) {
    if (this.canSendRemoteInstruction()) {
      return;
    }

    const { materialStore } = this;
    const material = materialStore.materials.find((v: IMaterial) => v.pumpId === pump.id);
    const md = material ? materialStore.materialDetails[material.materialDetailId] : null;

    if (md) {
      if (this.running.length) {
        const pumpStatus = this.getDeviceStatus(pump.id);
        const runningData = this.running?.find(v => v.uid === pumpStatus.uid && v.currentDrop === pumpStatus.currentDrop);
        if (runningData) {
          runningData.manualLoad = true;
        }
      } else {
        this.resetMeter(pump.meterPNo);
      }

      // @ts-ignore
      this[`pump-button-release-timer-${pump.id}`] = this.setTimeout(() => {
        const pumpStatus = this.getDeviceStatus(pump.id);
        this.deviceStatus.set(pump.id, {
          ...pumpStatus,
          manualFeedStarted: false,
        });
      }, 3500);

    }
  },

  isOtherBinStillFeeding(this: IBatchStore, bin: IBin) {
    let isFeeding = false;
    const bins = this.devicesStore.bins.filter((b: IBin) => b.scaleId === bin.scaleId && b.id !== bin.id);

    bins.forEach((b: IBin) => {
      const s = this.getDeviceStatus(b.id);

      if ([DeviceStatus.FEED, DeviceStatus.SETTLING, DeviceStatus.JOG, DeviceStatus.DELAY, DeviceStatus.HOLD, DeviceStatus.MANUAL].includes(s.status)) {
        isFeeding = true;
        logger.log('other bin still feeding:', b.id, b.description);
      }
    });

    return isFeeding;
  },

  onBinButtonDown(this: IBatchStore, binId: number) {
    this.pressedBinButtons.add(binId);
  },

  onBinButtonUp(this: IBatchStore, binId: number) {
    this.pressedBinButtons.delete(binId);
  },

  isBinButtonPressed(this: IBatchStore, binId: number) {
    return this.pressedBinButtons.has(binId);
  },

  onBinButtonPress(this: IBatchStore, bin: IBin) {
    const { settingsStore } = this.rootStore;
    const { configuration } = settingsStore;

    if (this.canSendRemoteInstruction()) {
      return;
    }

    if (this.maintenanceMode && !configuration?.reduceMaintenanceModeInventory) {
      this.setOutput(bin.gateOnePNo, this.isOutputOn(bin.gateOnePNo) ? '0' : OUTPUT_TTL);
      return;
    }

    const binStatus = this.getDeviceStatus(bin.id);

    // Check if the bin is currently jogging (JOG status or SETTLING with previous JOG status)
    const isJogging = binStatus.status === DeviceStatus.JOG ||
      (binStatus.status === DeviceStatus.SETTLING && binStatus.prevStatus === DeviceStatus.JOG);

    if (isJogging) {
      // If jogging, first hold the device (simulating hold button press)
      logger.log(`bin #${bin.id} '${bin.description}' is jogging, holding device first`);
      this.holdFeedDevice(bin.id);

      // After the continuous press duration, simulate the manual feed button press
      // This ensures the exact same flow as pressing hold then feed buttons separately
      this.timers.set(timerKey.continuousPress(bin.id), () => {
        // Only proceed if button is still pressed
        if (this.isBinButtonPressed(bin.id)) {
          logger.log(`bin #${bin.id} '${bin.description}' button still pressed, starting manual feed`);
          // Call the regular button press logic which handles manual feed properly
          // First mark that we're not jogging anymore to allow manual feed to proceed
          const currentBinStatus = this.getDeviceStatus(bin.id);
          this.deviceStatus.set(bin.id, {
            ...currentBinStatus,
            wasJogging: true,  // Track that we were jogging for resume later
          });
          // Now trigger the normal manual feed flow
          this.onBinButtonPress(bin);
        }
      }, CONTINUOUS_FEED_BUTTON_PRESS_DURATION, `Continuous press bin #${bin.id}`);

      return;
    }

    if (
      binStatus.status === DeviceStatus.SETTLING && binStatus.prevStatus !== DeviceStatus.JOG
    ) {
      return;
    }

    const scale = this.devicesStore.scales.find((v: IScale) => v.id === bin.scaleId);
    if (scale && this.isScaleOpen(scale)) {
      logger.log(`bin #${bin.id} '${bin.description}' manual feed disabled because scale: '${scale.description}' is open!`);
      return;
    }
    if (scale && this.isDoubleGateScale(scale) && this.isScaleOpen(scale, scale.gateTwoStatusPNo)) {
      logger.log(`bin #${bin.id} '${bin.description}' manual feed disabled because scale: '${scale.description}' is open, second gate!`);
      return;
    }

    if (!this.isOtherBinStillFeeding(bin) && [DeviceStatus.IDLE, DeviceStatus.FINISH].includes(binStatus.status)) {
      this.deviceStatus.set(bin.id, {
        ...binStatus,
        manualStartWeight: this.getCurrentWeightForBin(bin.id) || 0,
        isManual: true,
        prevStatus: binStatus.status,
        error: false,
        status: DeviceStatus.FEED,
        manualFeedStarted: true,
      });
    }

    this.setFeedInterruption(bin.id);

    this.setOutput(bin.gateOnePNo, OUTPUT_TTL);
  },

  setFeedInterruption(this: IBatchStore, binId: number) {
    if (this.running.length && !this.replayMode && this.canEmit) {
      const r = this.running[0];
      const binStatus = this.getDeviceStatus(binId);

      if (r.uid === binStatus.uid && r.currentDrop === binStatus.currentDrop && r.scales) {
        Object.keys(r.scales).forEach((scaleId) => {
          r.scales[scaleId].forEach((data: IWeighedDataToBatch) => {
            if (data.bin?.id === binId) {
              data.isFeedInterrupted = true;
              logger.log('feed interrupted', data.material?.code, data.bin?.description);
            }
          });
        });
      }
    }
  },

  onBinButtonRelease(this: IBatchStore, bin: IBin): void {
    const { settingsStore } = this.rootStore;
    const { configuration } = settingsStore;

    if (this.canSendRemoteInstruction() || (this.maintenanceMode && !configuration?.reduceMaintenanceModeInventory)) {
      return;
    }

    // Clear any pending continuous press timer for jogging scenarios
    this.timers.clear(timerKey.continuousPress(bin.id));

    const binStatus = this.getDeviceStatus(bin.id);
    const material = this.materialStore.getMaterialForBin(bin.id);
    const materialDetails = material ? this.materialStore.materialDetails[material.materialDetailId] : null;
    const scaleSettlingTime = materialDetails ? materialDetails.scaleSettlingTime : 2;

    if (!binStatus.manualFeedStarted) {
      return;
    }

    const scale = this.devicesStore.getDeviceById(bin.scaleId);

    if (!scale || !materialDetails || this.isOtherBinStillFeeding(bin)) {
      this.deviceStatus.set(bin.id, {
        ...binStatus,
        manualFeedStarted: false,
        manualStartWeight: 0,
        isManual: false,
        status: binStatus.prevStatus || DeviceStatus.IDLE,
      });

      return;
    }

    this.deviceStatus.set(bin.id, {
      ...binStatus,
      manualFeedStarted: false,
      status: DeviceStatus.SETTLING,
    });

    this.timers.set(timerKey.device(bin.id), () => {
      const weight = this.getScaleWeight(scale);
      const binStatus = this.getDeviceStatus(bin.id);
      const quantity = weight - binStatus.manualStartWeight;
      const quantityConverted = toNumber(convertUnit(quantity, this.getUnitById(scale.unitId), material.itemInventoryUnitSymbol), 4);
      logger.log('manual feed', material.code, quantity, weight, binStatus.manualStartWeight);
      logger.log('update material', material.code, quantityConverted, material.itemInventoryUnitSymbol);

      if (this.checkIndicatorValueError(weight) === DeviceStatus.INDICATOR_ERROR) {
        logger.log('indicator error', scale.id, scale.description);
        return;
      }

      if (this.isOtherBinStillFeeding(bin)) {
        logger.log('other bin is feeding, ignoring the inventory for manual feed', bin?.description, material.code);
        this.deviceStatus.set(bin.id, {
          ...binStatus,
          manualFeedStarted: false,
          manualStartWeight: 0,
          isManual: false,
          status: binStatus.prevStatus || DeviceStatus.IDLE,
        });

        return;
      }

      if (this.canReduceInventory && quantityConverted > 0) {
        this.materialStore.updateMaterialInventoryUse(material, {
          quantity: quantityConverted,
          comment: 'Manual CB',
          isAutomatic: false,
          binIds: [ bin.id ],
        });
      }

      if (binStatus.uid && this.running.length) {
        const index = this.running.findIndex(v => v.uid === binStatus.uid && v.currentDrop === binStatus.currentDrop);

        if (index > -1) {
          const runningData = this.running[index];
          runningData.manualLoad = true;
          const mixerStatus = this.getDeviceStatus(runningData.mixerId);
          const shouldUpdateTicket = !runningData.mixerId
            || !ALLOWED_MIXER_STATUSES_FOR_NEXT_TICKET_BATCHING.includes(mixerStatus.status)
            || runningData.uid !== mixerStatus.uid
            || runningData.currentDrop !== mixerStatus.currentDrop;

          if (runningData.mixDesign && shouldUpdateTicket) {
            const scaleUnit = this.getUnitById(scale.unitId);
            let qty = quantity;

            runningData.mixDesign.forEach((v: MixDesignItem, i: number) => {
              if ((v.batchCode && v.batchCode === material.batchCode) || v.constituentItemId === material.itemId) {

                if (v.unitSymbol !== scaleUnit) {
                  qty = toNumber(convertUnit(qty, scaleUnit, v.unitSymbol), 2);
                }

                runningData.mixDesign[i].actualWeight = (v.actualWeight || 0) + qty;
                runningData.mixDesign[i].manualWeight = (v.manualWeight || 0) + qty;

                logger.log('manual feed:', material.code, qty, runningData.mixDesign[i].manualWeight, v.unitSymbol, scaleUnit);
              }
            });
          }
        }
      }

      this.deviceStatus.set(bin.id, {
        ...binStatus,
        weight: binStatus.prevStatus === DeviceStatus.IDLE ? 0 : binStatus.weight + quantity,
        manualStartWeight: 0,
        isManual: false,
        wasJogging: false,
        status: binStatus.prevStatus === DeviceStatus.SETTLING ? DeviceStatus.IDLE : binStatus.prevStatus,
      });

      // If the bin was jogging before manual feed, restart jogging
      if (binStatus.wasJogging) {
        // Find the running data and restart jogging
        const runningData = this.running.find(r =>
          r.uid === binStatus.uid && r.currentDrop === binStatus.currentDrop,
        );
        if (runningData?.scales) {
          const scaleData = runningData.scales[bin.scaleId];
          if (scaleData) {
            const materialData = scaleData.find((data: IWeighedDataToBatch) => data.bin?.id === bin.id);
            if (materialData) {
              // Restart jogging with the stored context
              this.jogMode(materialData, runningData, materialData.mft);
            }
          }
        }
      }

    }, (scaleSettlingTime + bin.jogCloseTime) * 1000);

  },

  isHoldFeedDevice(this: IBatchStore, deviceId: number): boolean {
    return this.holdFeedDevices.includes(deviceId);
  },

  onPausePlayFeedButtonPress(this: IBatchStore, deviceId: number) {
    if (this.isHoldFeedDevice(deviceId)) {
      this.unHoldFeedDevice(deviceId);
    } else {
      this.holdFeedDevice(deviceId);
    }
  },

  /**
   * @param updateInventory false when the bin is not pausing a feed of its own (e.g. a material substitution parks
   * the substitute bin); its status still describes an earlier feed, which must not be deducted again.
   */
  holdFeedDevice(this: IBatchStore, deviceId: number, updateInventory: boolean = true) {
    if (!this.isHoldFeedDevice(deviceId)) {
      const d = this.getDeviceStatus(deviceId);

      this.holdFeedDevices.push(deviceId);

      this.deviceStatus.set(deviceId, {
        ...d,
        prevStatus: d.status, // Preserve the actual previous status (e.g., JOG)
        status: DeviceStatus.HOLD,
      });

      this.stopAttachedDevices(deviceId);
      if (updateInventory) {
        this.updateInventoryOnPause(deviceId);
      }
    }
  },

  unHoldFeedDevice(this: IBatchStore, deviceId: number) {
    this.holdFeedDevices = this.holdFeedDevices.filter(id => id !== deviceId);
  },

  stopAttachedDevices(this: IBatchStore, deviceId: number) {
    const device = this.devicesStore.getDeviceById(deviceId);

    if (!device) {
      return;
    }

    if (device.aeratorId) {
      aeratorSwitch(this.rootStore, device.aeratorId, false);
    }

    if (device.vibratorId) {
      vibratorSwitch(this.rootStore, device.vibratorId, false);
    }
  },

  shouldOpenButtonBeDisabled(this: IBatchStore, scale: IScale): boolean {
    const { devicesStore } = this;
    const bins = devicesStore.bins.filter((v: IBin) => v.scaleId === scale.id);
    const scaleStatus = this.getDeviceStatus(scale.id);
    let disabled = false;

    if (this.maintenanceMode) {
      return false;
    }

    if (scaleStatus.status === DeviceStatus.WAIT_ADMIX) {
      disabled = true;
    }

    bins.forEach((b: IBin) => {
      const d = this.getDeviceStatus(b.id);

      if (this.running.length && [DeviceStatus.FEED, DeviceStatus.SETTLING, DeviceStatus.JOG].includes(d.status)) {
        disabled = true;
      }
    });

    if (scale.beltId && !this.isBeltOn(scale.beltId)) {
      disabled = true;
    }

    const mixer = this.devicesStore.mixers.length
      ? this.devicesStore.mixers.find((v: IMixer) => [MixerType.Stationary, MixerType.Central].includes(v.type))
      : null;

    if (mixer) {
      const isMixerDisabled = mixer.diverterPNo ? this.isOutputOn(mixer.diverterPNo) : false;
      if (!isMixerDisabled &&
        (!this.isMixerOn(mixer) || this.isInputPortOff(mixer.closeReturnStatusPNo) || !this.isInputPortOff(mixer.openTiltStatusPNo))
      ) {
        disabled = true;
      }
    }

    if (this.isInputPortOff(scale.interlockStatusPNo)) {
      disabled = true;
    }

    return disabled;
  },

  isFinishFeedDevice(this: IBatchStore, deviceId: number): boolean {
    return this.finishFeedDevices.includes(deviceId);
  },

  isFinishDischargeDevice(this: IBatchStore, deviceId: number): boolean {
    return isFinishDischargeDevice(this, deviceId);
  },

  onFinishFeedButtonPress(
    this: IBatchStore,
    deviceId: number,
    material: IMaterial,
    onConfirmed: () => void = () => {/* empty */},
  ): void {
    if (this.isFinishFeedDevice(deviceId)) {
      return;
    }

    let ds = this.getDeviceStatus(deviceId);
    const missingTarget = toNumber((ds.target || 0) - (ds.weight || 0), 2);
    let message;

    if (missingTarget > 0) {
      message = `Target for ${ds.materialCode} is ${ds.target} ${ds.unit}.
      You're missing ${missingTarget} ${ds.unit}.
      Are you sure you want to end this Target?`;
    } else {
      message = `Target for ${ds.materialCode} is ${ds.target} ${ds.unit}.
      Weight is over ${missingTarget*-1} ${ds.unit}.
      Are you sure you want to end this Target?`;
    }

    showConfirmModal({
      title: 'Warning!',
      message: message,
      okLabel: 'Yes',
      cancelLabel: 'No',
      enableEscape: false,
      onExited: () => {
        this.isAbortDialogVisible = false;
      },
    }).then(() => {
      ds = this.getDeviceStatus(deviceId);
      if (!this.isFinishFeedDevice(deviceId) && ds.status !== DeviceStatus.FINISH) {
        if (this.canSendRemoteInstruction()) {
          this.sendRemoteInstruction(RemoteBatchData(RemoteControlEventType.FINISH_FEED_DEVICE, null, {
            deviceId,
          }));
          return;
        } else {
          this.finishFeedDevices.push(deviceId);
        }
        onConfirmed();
      }
    }).catch(() =>{
      logger.log('user cancel finishing pump feed', material?.code);
    });
  },

  onFinishDischargeButtonPress(
    this: IBatchStore,
    deviceId: number,
    onConfirmed: () => void = () => {/* empty */},
  ): void {
    onFinishDischargeButtonPress(this, deviceId, onConfirmed);
  },

  onPumpResetButtonClick(this: IBatchStore, pump: IPump): void {
    if (pump.meterPNo) {
      this.resetMeter(pump.meterPNo);
      this.resetCounter(pump.meterPNo);
      this.setTimeout(() => {
        this.setPumpWeight(pump.id);
      }, 1000);
    }
  },

};
